import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Автообнаружение устройств (ТЗ §11, фаза 9, задача 3).
 *
 * Агент сканирует подсеть площадки (ARP + ICMP + необязательный TCP-скан
 * типовых портов), платформа складывает результат и **предлагает** добавить
 * найденное на карту. Ничего не создаётся без подтверждения: критерий
 * приёмки на этом настаивает отдельно, и не зря — сканирование чужой сети
 * находит и принтеры соседнего отдела, и телефоны в гостевом VLAN.
 */
/**
 * Наибольшая подсеть для одного прогона — /20, то есть 4094 адреса.
 *
 * Предел не в производительности, а в осмысленности: /16 это 65 тысяч
 * адресов, прогон на полчаса и список, в котором ничего не найти. Площадку
 * такого размера сканируют по частям, и это правильнее, чем один прогон,
 * который непонятно, когда закончится.
 */
export const MIN_SCAN_PREFIX = 20;
/** Наибольшее число найденных хостов в одном ответе. */
export const MAX_DISCOVERED_HOSTS = 4096;
/**
 * Типовые порты, по которым определяют, что за устройство найдено.
 *
 * Список короткий намеренно: это подсказка «похоже на камеру», а не
 * инвентаризация служб. Полное сканирование портов — работа сканера
 * безопасности, и делать его частью мониторинга значило бы устраивать
 * в чужой сети то, что служба безопасности заказчика видит как атаку.
 */
export const TYPICAL_TCP_PORTS = [22, 80, 443, 445, 554, 3389, 5900, 8000, 8080, 9100];
/**
 * По открытым портам угадывается тип узла — но только как подсказка.
 *
 * Значения — ключи словаря, а не подписи: имя порта видит человек, а язык,
 * на котором он его видит, знает только браузер (§11, фаза 10, задача 4).
 * Большинство имён совпадает в обоих языках, но «печать» и «HTTP (камеры)»
 * не совпадают, а разнобой — половина ключей, половина текста — обошёлся бы
 * дороже единообразия.
 */
export const PORT_HINTS = {
    22: 'discovery.port.ssh',
    80: 'discovery.port.http',
    443: 'discovery.port.https',
    445: 'discovery.port.smb',
    554: 'discovery.port.rtsp',
    3389: 'discovery.port.rdp',
    5900: 'discovery.port.vnc',
    8000: 'discovery.port.cameraHttp',
    8080: 'discovery.port.http',
    9100: 'discovery.port.printing',
};
/**
 * Адрес подсети в форме CIDR.
 *
 * Проверяется строкой, а не разбором на числа: разбор нужен агенту,
 * а здесь достаточно отсеять то, что подсетью вообще не является,
 * и не пустить прогон на всю сеть.
 */
export const cidrSchema = z
    .string()
    .trim()
    .regex(/^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/, messageKey('validation.discovery.cidrFormat'))
    .refine((value) => {
    const [address, prefix] = value.split('/');
    const octets = (address ?? '').split('.').map(Number);
    return (octets.length === 4 &&
        octets.every((octet) => Number.isInteger(octet) && octet >= 0 && octet <= 255) &&
        Number(prefix) >= MIN_SCAN_PREFIX &&
        Number(prefix) <= 32);
}, messageKey('validation.discovery.cidrTooWide', { prefix: MIN_SCAN_PREFIX }));
export const startDiscoverySchema = z.object({
    /** Агент, который сканирует. Не задан — берётся единственный на площадке. */
    agentId: z.uuid().optional(),
    cidr: cidrSchema,
    /**
     * Порты для проверки на отозвавшихся хостах. Пустой список — TCP-скан
     * не выполняется вовсе: он необязателен по ТЗ и в чужой сети может
     * оказаться нежелательным.
     */
    tcpPorts: z.array(z.int().min(1).max(65_535)).max(20).default([]),
    /** Предел ожидания одного адреса, мс. */
    timeoutMs: z.int().min(100).max(5000).default(700),
});
export const discoveredHostSchema = z.object({
    ip: z.string().min(1).max(64),
    /** MAC из таблицы соседей; `null` — хост не в том же сегменте. */
    mac: z.string().max(64).nullable(),
    hostname: z.string().max(255).nullable(),
    /** Ответил ли на ICMP. Хост может найтись только по ARP или по порту. */
    respondedToIcmp: z.boolean(),
    rttMs: z.number().nonnegative().nullable(),
    openPorts: z.array(z.int().min(1).max(65_535)).max(20),
});
/**
 * Найденный хост вместе с тем, что о нём знает платформа.
 *
 * `knownNodeId` отвечает на единственный вопрос, который задают этому
 * списку: «а этот уже на карте?». Без него после третьего сканирования
 * половина списка была бы дублями уже заведённых узлов.
 */
export const discoveryCandidateSchema = discoveredHostSchema.extend({
    knownNodeId: z.uuid().nullable(),
    knownNodeName: z.string().nullable(),
    /** Подсказка по открытым портам: «камера», «принтер». `null` — не угадали. */
    guess: z.string().nullable(),
});
export const DISCOVERY_STATUSES = ['running', 'done', 'failed'];
export const discoveryScanSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    agentId: z.uuid(),
    agentName: z.string(),
    cidr: z.string(),
    tcpPorts: z.array(z.int()),
    status: z.enum(DISCOVERY_STATUSES),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    /** Сколько адресов агент обошёл. Показывает, что прогон живой. */
    scannedCount: z.number().int().nonnegative(),
    candidates: z.array(discoveryCandidateSchema),
    /** Причина отказа простым языком. */
    error: z.string().nullable(),
});
// — Управляющие сообщения (§9.3) ——————————————————————————————
/** Сервер → агент: просканировать подсеть. */
export const discoveryScanMessageSchema = z.object({
    t: z.literal('discovery.scan'),
    scanId: z.uuid(),
    cidr: z.string().min(1).max(64),
    tcpPorts: z.array(z.int().min(1).max(65_535)).max(20),
    timeoutMs: z.int().min(100).max(5000),
});
/**
 * Агент → сервер: результат.
 *
 * `error` вместо `hosts` — это тоже результат: «агент не смог» и «агент
 * не нашёл ничего» различаются, и показывать первое как пустой список
 * значило бы сказать «в подсети никого нет», когда в ней просто не сканировали.
 */
export const discoveryResultMessageSchema = z.object({
    t: z.literal('discovery.result'),
    scanId: z.uuid(),
    hosts: z.array(discoveredHostSchema).max(MAX_DISCOVERED_HOSTS).default([]),
    scannedCount: z.number().int().nonnegative().default(0),
    error: z.string().max(500).nullable().default(null),
});
// — Подсказки ————————————————————————————————————————————
/**
 * Что это за устройство, судя по открытым портам.
 *
 * Именно «судя»: 554 у камеры и 554 у видеорегистратора выглядят одинаково,
 * а машина с RDP может оказаться сервером. Поэтому подсказка показывается
 * подписью рядом с выбором типа, а тип всё равно выбирает человек.
 *
 * Возвращается ключ словаря: догадку вычисляет `api`, разбирая ответ агента,
 * а читает её браузер — и на своём языке, а не на языке сервера.
 */
export function guessKind(openPorts) {
    const ports = new Set(openPorts);
    if (ports.has(554)) {
        return 'discovery.guess.camera';
    }
    if (ports.has(9100)) {
        return 'discovery.guess.printer';
    }
    if (ports.has(3389)) {
        return 'discovery.guess.rdp';
    }
    if (ports.has(5900)) {
        return 'discovery.guess.vnc';
    }
    if (ports.has(445)) {
        return 'discovery.guess.windows';
    }
    if (ports.has(22)) {
        return 'discovery.guess.ssh';
    }
    if (ports.has(80) || ports.has(443) || ports.has(8080) || ports.has(8000)) {
        return 'discovery.guess.web';
    }
    return null;
}
/**
 * Названия открытых портов для подсказки в таблице.
 *
 * Переводчик передаётся снаружи: словарь живёт в браузере, а сборка строки —
 * здесь, рядом со списком портов. Без него возвращаются ключи — это видно
 * на экране сразу и не выглядит правдоподобной подписью, в отличие от
 * молчаливого возврата к языку сервера.
 */
export function describePorts(openPorts, translate = (key) => key) {
    if (openPorts.length === 0) {
        return '';
    }
    return openPorts
        .map((port) => {
        const hint = PORT_HINTS[port];
        return hint ? `${String(port)} (${translate(hint)})` : String(port);
    })
        .join(', ');
}
/**
 * Сколько адресов в подсети без учёта сети и широковещательного.
 *
 * Нужно интерфейсу, чтобы сказать «будет опрошено 254 адреса» до запуска:
 * человек, набравший /20 вместо /24, должен увидеть это до, а не после.
 */
export function addressCount(cidr) {
    const prefix = Number(cidr.split('/')[1]);
    if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) {
        return 0;
    }
    if (prefix >= 31) {
        // /31 и /32 — особые случаи: сети и широковещательного адреса там нет.
        return 2 ** (32 - prefix);
    }
    return 2 ** (32 - prefix) - 2;
}
//# sourceMappingURL=discovery.js.map